/* ==========================================================================
   E.M.P.R.I.C.S Motion Engine
   Cinematic scroll-driven animations, parallax, counters, reveals
   ========================================================================== */
(function () {
  'use strict';

  var scrollY = 0;
  var ticking = false;
  var wH = window.innerHeight;
  var wW = window.innerWidth;

  /* ---- Utilities ---- */
  function lerp(a, b, t) { return a + (b - a) * t; }
  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
  function easeOutExpo(t) { return t === 1 ? 1 : 1 - Math.pow(2, -10 * t); }

  /* ---- 1. Parallax ---- */
  var parallaxEls = [];
  function initParallax() {
    parallaxEls = Array.from(document.querySelectorAll('[data-parallax]'));
  }
  function tickParallax() {
    for (var i = 0; i < parallaxEls.length; i++) {
      var el = parallaxEls[i];
      var speed = parseFloat(el.dataset.parallax) || 0.3;
      var offset = scrollY * speed;
      el.style.transform = 'translate3d(0,' + offset + 'px,0)';
    }
  }

  /* ---- 2. Scroll Progress ---- */
  var progressEls = [];
  function initScrollProgress() {
    progressEls = Array.from(document.querySelectorAll('[data-scroll-progress]'));
  }
  function tickProgress() {
    for (var i = 0; i < progressEls.length; i++) {
      var el = progressEls[i];
      var rect = el.getBoundingClientRect();
      var p = clamp((wH - rect.top) / (wH + rect.height), 0, 1);
      el.style.setProperty('--progress', p.toFixed(4));
    }
  }

  /* ---- 3. Reveal (IntersectionObserver) ---- */
  function initReveals() {
    var els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.dataset.revealDelay, 10) || 0;
        var duration = parseInt(el.dataset.revealDuration, 10) || 800;

        setTimeout(function () {
          el.style.transitionDuration = duration + 'ms';
          el.style.transitionTimingFunction = 'cubic-bezier(0.16,1,0.3,1)';
          el.classList.add('is-revealed');
        }, delay);

        if (!el.hasAttribute('data-reveal-repeat')) io.unobserve(el);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- 4. Counters ---- */
  function initCounters() {
    var els = document.querySelectorAll('[data-count-to]');
    if (!els.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || entry.target._counted) return;
        entry.target._counted = true;
        var el = entry.target;
        var target = parseFloat(el.dataset.countTo) || 0;
        var prefix = el.dataset.countPrefix || '';
        var suffix = el.dataset.countSuffix || '';
        var dur = parseFloat(el.dataset.countDuration) || 2000;
        var start = performance.now();

        function step(now) {
          var t = clamp((now - start) / dur, 0, 1);
          var val = Math.round(target * easeOutExpo(t));
          el.textContent = prefix + val.toLocaleString() + suffix;
          if (t < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }, { threshold: 0.2 });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- 5. Text Split ---- */
  function initSplitText() {
    var els = document.querySelectorAll('[data-split-text]');
    els.forEach(function (el) {
      if (el._split) return;
      el._split = true;
      var mode = el.dataset.split || 'chars';
      var html = '';
      if (mode === 'words') {
        el.textContent.split(/\s+/).forEach(function (w, i) {
          html += '<span class="split-word" style="display:inline-block;opacity:0;transform:translateY(12px);transition:all 0.6s cubic-bezier(0.16,1,0.3,1) ' + (i * 0.06) + 's">' + w + '</span> ';
        });
      } else {
        el.textContent.split('').forEach(function (c, i) {
          if (c === ' ') { html += '<span>&nbsp;</span>'; return; }
          html += '<span class="split-char" style="display:inline-block;opacity:0;transform:translateY(12px);transition:all 0.5s cubic-bezier(0.16,1,0.3,1) ' + (i * 0.03) + 's">' + c + '</span>';
        });
      }
      el.innerHTML = html;

      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var spans = entry.target.querySelectorAll('.split-char,.split-word');
          spans.forEach(function (s) { s.style.opacity = '1'; s.style.transform = 'translateY(0)'; });
          io.unobserve(entry.target);
        });
      }, { threshold: 0.2 });
      io.observe(el);
    });
  }

  /* ---- 6. Magnetic Cursor ---- */
  function initMagnetic() {
    if (!window.matchMedia('(pointer:fine)').matches) return;
    var els = document.querySelectorAll('.magnetic');
    if (!els.length) return;

    document.addEventListener('mousemove', function (e) {
      els.forEach(function (el) {
        var r = el.getBoundingClientRect();
        var cx = r.left + r.width / 2;
        var cy = r.top + r.height / 2;
        var dx = e.clientX - cx;
        var dy = e.clientY - cy;
        var dist = Math.sqrt(dx * dx + dy * dy);
        var maxD = Math.max(r.width, r.height);

        if (dist < maxD) {
          var s = 1 - dist / maxD;
          el.style.transform = 'translate3d(' + (dx * s * 0.15) + 'px,' + (dy * s * 0.15) + 'px,0)';
        } else {
          el.style.transform = 'translate3d(0,0,0)';
        }
      });
    }, { passive: true });
  }

  /* ---- 7. Image Reveal ---- */
  function initImageReveal() {
    var els = document.querySelectorAll('[data-image-reveal]');
    if (!els.length) return;

    var clips = {
      left: ['inset(0 100% 0 0)', 'inset(0 0 0 0)'],
      right: ['inset(0 0 0 100%)', 'inset(0 0 0 0)'],
      top: ['inset(0 0 100% 0)', 'inset(0 0 0 0)'],
      bottom: ['inset(100% 0 0 0)', 'inset(0 0 0 0)']
    };

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var dir = el.dataset.imageReveal || 'left';
        var pair = clips[dir] || clips.left;
        el.style.clipPath = pair[0];
        requestAnimationFrame(function () {
          el.style.transition = 'clip-path 1s cubic-bezier(0.16,1,0.3,1)';
          el.style.clipPath = pair[1];
        });
        io.unobserve(el);
      });
    }, { threshold: 0.15 });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- 8. Stagger groups ---- */
  function initStagger() {
    var els = document.querySelectorAll('[data-emprics-stagger]');
    if (!els.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var idx = parseInt(el.dataset.empricsStagger, 10) || 0;
        el.style.transitionDelay = (idx * 100) + 'ms';
        el.classList.add('is-revealed');
        io.unobserve(el);
      });
    }, { threshold: 0.1 });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- Main Loop ---- */
  function onScroll() {
    scrollY = window.scrollY;
    if (!ticking) {
      requestAnimationFrame(function () {
        tickParallax();
        tickProgress();
        ticking = false;
      });
      ticking = true;
    }
  }

  function onResize() {
    wH = window.innerHeight;
    wW = window.innerWidth;
  }

  function init() {
    initParallax();
    initScrollProgress();
    initReveals();
    initCounters();
    initSplitText();
    initMagnetic();
    initImageReveal();
    initStagger();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    scrollY = window.scrollY;
    tickParallax();
    tickProgress();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
